import type { ComponentType } from "react";

import { AccessibilityIcon } from "./accessibility.icon";
import { ArrowLeftIcon } from "./arrowLeft.icon";
import { ArrowRightIcon } from "./arrowRight.icon";
import { BackedByADTIcon } from "./backedByADT.icon";
import { CameraIcon } from "./camera.icon";
import { CamerasIcon } from "./cameras.icon";
import { CartIcon } from "./cart.icon";
import { CelebrateIcon } from "./celebrate.icon";
import { ChatIcon } from "./chat.icon";
import { CheckIcon } from "./check.icon";
import { CheckmarkThinIcon } from "./checkmarkThin.icon";
import { ChevronDownIcon } from "./chevronDown.icon";
import { ChevronDownThinIcon } from "./chevronDownThin.icon";
import { ChevronLeftIcon } from "./chevronLeft.icon";
import { ChevronRightIcon } from "./chevronRight.icon";
import { ChevronRightThinIcon } from "./chevronRightThin.icon";
import { ChevronUpIcon } from "./chevronUp.icon";
import { CloseIcon } from "./close.icon";
import { EasyDIYIcon } from "./easyDIY.icon";
import { ExternalLinkIcon } from "./externalLink.icon";
import { ForwardSlash } from "./forwardSlash.icon";
import { ThirtyDayIcon } from "./free-monitoring/30day.icon";
import { ActivateIcon } from "./free-monitoring/activate.icon";
import { AfterThirtyDayIcon } from "./free-monitoring/after30day.icon";
import { FreeShippingIcon } from "./freeShippingIcon.component";
import { HeadsetIcon } from "./headset.icon";
import { InfoIcon } from "./info.icon";
import LinkIcon from "./link.icon";
import { MenuIcon } from "./menu.icon";
import { MinusIcon } from "./minus.icon";
import { NoLongTermIcon } from "./noLongTerm.icon";
import { PauseIcon } from "./pause.icon";
import { AffirmLogoIcon } from "./payment/affirmLogo.icon";
import { CreditCardAmexLogoIcon } from "./payment/creditCardAmexLogo.icon";
import { CreditCardMastercardLogoIcon } from "./payment/creditCardMastercardLogo.icon";
import { CreditCardVisaLogoIcon } from "./payment/creditCardVisaLogo.icon";
import { PhoneIcon } from "./phone.icon";
import { PlayIcon } from "./play.icon";
import { PlusIcon } from "./plus.icon";
import { Loading1Icon } from "./quiz/loading1.icon";
import { Loading2Icon } from "./quiz/loading2.icon";
import { Loading3Icon } from "./quiz/loading3.icon";
import { Loading4Icon } from "./quiz/loading4.icon";
import { QuoteIcon } from "./quote.icon";
import { SaleIcon } from "./sale.icon";
import { SearchIcon } from "./search.icon";
import { FacebookIcon } from "./social/facebook.icon";
import { InstagramIcon } from "./social/instagram.icon";
import { PinterestIcon } from "./social/pinterest.icon";
import { TwitterIcon } from "./social/twitter.icon";
import { YoutubeIcon } from "./social/youtube.icon";
import { StarIcon } from "./star.icon";
import { TagIcon } from "./tag.icon";
import { TooltipArrowIcon } from "./tooltipArrow.icon";
import { TrashIcon } from "./trash.icon";
import { ClockIcon } from "./clock.icon";
import { SaleOutlineIcon } from "./saleOutline.icon";

/**
 * Single source of truth for storefront icons (Studio picker + RichText).
 * Keys are stable IDs matching icon file paths (without suffix).
 */
export const FRONTEND_ICON_GROUPS = [
  "general",
  "social",
  "payment",
  "quiz",
  "free-monitoring",
] as const;

export type FrontendIconGroup = (typeof FRONTEND_ICON_GROUPS)[number];

export const FRONTEND_ICON_GROUP_TITLES: Record<FrontendIconGroup, string> = {
  general: "General",
  social: "Social",
  payment: "Payment",
  quiz: "Quiz",
  "free-monitoring": "Free monitoring",
};

type IconRegistryEntry = {
  title: string;
  group: FrontendIconGroup;
  Icon: ComponentType<IconProps>;
};

export const ICON_REGISTRY = {
  accessibility: {
    title: "Accessibility",
    group: "general",
    Icon: AccessibilityIcon,
  },
  arrowLeft: { title: "Arrow left", group: "general", Icon: ArrowLeftIcon },
  arrowRight: { title: "Arrow right", group: "general", Icon: ArrowRightIcon },
  backedByADT: {
    title: "Backed by ADT",
    group: "general",
    Icon: BackedByADTIcon,
  },
  cart: { title: "Cart", group: "general", Icon: CartIcon },
  celebrate: { title: "Celebrate", group: "general", Icon: CelebrateIcon },
  chat: { title: "Chat", group: "general", Icon: ChatIcon },
  check: { title: "Check", group: "general", Icon: CheckIcon },
  checkmarkThin: {
    title: "Checkmark (thin)",
    group: "general",
    Icon: CheckmarkThinIcon,
  },
  chevronDown: {
    title: "Chevron down",
    group: "general",
    Icon: ChevronDownIcon,
  },
  chevronDownThin: {
    title: "Chevron down (thin)",
    group: "general",
    Icon: ChevronDownThinIcon,
  },
  chevronLeft: {
    title: "Chevron left",
    group: "general",
    Icon: ChevronLeftIcon,
  },
  chevronRight: {
    title: "Chevron right",
    group: "general",
    Icon: ChevronRightIcon,
  },
  chevronRightThin: {
    title: "Chevron right (thin)",
    group: "general",
    Icon: ChevronRightThinIcon,
  },
  chevronUp: { title: "Chevron up", group: "general", Icon: ChevronUpIcon },
  close: { title: "Close", group: "general", Icon: CloseIcon },
  easyDIY: { title: "Easy DIY", group: "general", Icon: EasyDIYIcon },
  externalLink: {
    title: "External link",
    group: "general",
    Icon: ExternalLinkIcon,
  },
  forwardSlash: {
    title: "Forward slash",
    group: "general",
    Icon: ForwardSlash,
  },
  freeShipping: {
    title: "Free shipping",
    group: "general",
    Icon: FreeShippingIcon,
  },
  headset: { title: "Headset", group: "general", Icon: HeadsetIcon },
  info: { title: "Info", group: "general", Icon: InfoIcon },
  camera: { title: "Camera", group: "general", Icon: CameraIcon },
  cameras: { title: "Cameras", group: "general", Icon: CamerasIcon },
  link: { title: "Link", group: "general", Icon: LinkIcon },
  menu: { title: "Menu", group: "general", Icon: MenuIcon },
  minus: { title: "Minus", group: "general", Icon: MinusIcon },
  noLongTerm: { title: "No long term", group: "general", Icon: NoLongTermIcon },
  pause: { title: "Pause", group: "general", Icon: PauseIcon },
  phone: { title: "Phone", group: "general", Icon: PhoneIcon },
  play: { title: "Play", group: "general", Icon: PlayIcon },
  plus: { title: "Plus", group: "general", Icon: PlusIcon },
  quote: { title: "Quote", group: "general", Icon: QuoteIcon },
  sale: { title: "Sale", group: "general", Icon: SaleIcon },
  saleOutline: {
    title: "Sale Outline",
    group: "general",
    Icon: SaleOutlineIcon,
  },
  clock: { title: "Clock", group: "general", Icon: ClockIcon },
  search: { title: "Search", group: "general", Icon: SearchIcon },
  star: { title: "Star", group: "general", Icon: StarIcon },
  tag: { title: "Tag", group: "general", Icon: TagIcon },
  tooltipArrow: {
    title: "Tooltip arrow",
    group: "general",
    Icon: TooltipArrowIcon,
  },
  trash: { title: "Trash", group: "general", Icon: TrashIcon },
  "social/facebook": {
    title: "Facebook",
    group: "social",
    Icon: FacebookIcon,
  },
  "social/instagram": {
    title: "Instagram",
    group: "social",
    Icon: InstagramIcon,
  },
  "social/pinterest": {
    title: "Pinterest",
    group: "social",
    Icon: PinterestIcon,
  },
  "social/twitter": { title: "Twitter", group: "social", Icon: TwitterIcon },
  "social/youtube": { title: "YouTube", group: "social", Icon: YoutubeIcon },
  "payment/affirmLogo": {
    title: "Affirm",
    group: "payment",
    Icon: AffirmLogoIcon,
  },
  "payment/creditCardAmexLogo": {
    title: "American Express",
    group: "payment",
    Icon: CreditCardAmexLogoIcon,
  },
  "payment/creditCardMastercardLogo": {
    title: "Mastercard",
    group: "payment",
    Icon: CreditCardMastercardLogoIcon,
  },
  "payment/creditCardVisaLogo": {
    title: "Visa",
    group: "payment",
    Icon: CreditCardVisaLogoIcon,
  },
  "quiz/loading1": {
    title: "Quiz loading 1",
    group: "quiz",
    Icon: Loading1Icon,
  },
  "quiz/loading2": {
    title: "Quiz loading 2",
    group: "quiz",
    Icon: Loading2Icon,
  },
  "quiz/loading3": {
    title: "Quiz loading 3",
    group: "quiz",
    Icon: Loading3Icon,
  },
  "quiz/loading4": {
    title: "Quiz loading 4",
    group: "quiz",
    Icon: Loading4Icon,
  },
  "free-monitoring/30day": {
    title: "30 day",
    group: "free-monitoring",
    Icon: ThirtyDayIcon,
  },
  "free-monitoring/after30day": {
    title: "After 30 day",
    group: "free-monitoring",
    Icon: AfterThirtyDayIcon,
  },
  "free-monitoring/activate": {
    title: "Activate",
    group: "free-monitoring",
    Icon: ActivateIcon,
  },
} as const satisfies Record<string, IconRegistryEntry>;

export type FrontendIconName = keyof typeof ICON_REGISTRY;

export const FRONTEND_ICONS = (
  Object.keys(ICON_REGISTRY) as FrontendIconName[]
).map((value) => ({
  title: ICON_REGISTRY[value].title,
  value,
  group: ICON_REGISTRY[value].group,
  Icon: ICON_REGISTRY[value].Icon,
}));

export const FRONTEND_ICON_OPTIONS = FRONTEND_ICONS.map(({ title, value }) => ({
  title,
  value,
}));

export function isFrontendIconName(name: string): name is FrontendIconName {
  return Object.hasOwn(ICON_REGISTRY, name);
}

export function getFrontendIconTitle(
  value: string | undefined,
): string | undefined {
  if (!value || !isFrontendIconName(value)) return undefined;
  return ICON_REGISTRY[value].title;
}

export function getFrontendIconPreview(name: string | undefined) {
  if (!name || !isFrontendIconName(name)) return undefined;
  return ICON_REGISTRY[name].Icon;
}
